import api from './config';
import { buildFeedQuery } from '@/utils/feed';
import type { RecommendedTracksResponse, Track } from '@/types';

/** Deezer track info by ISRC. */
export const getTrackInfo = async (isrc: string): Promise<Track> => {
  const response = await api.get(`/external-service-access/deezer/track-info/${isrc}`);
  return response.data;
};

/**
 * Recommended tracks for the home feed.
 *
 * NOTE: the current backend's recommendation schema is pre-Groups — it has no
 * `groups` relation and no `isActive` field (only `is_public`, `sender`,
 * `track`, `comments`). So we query the schema that exists today:
 *  - groupId (+/- includePublic) → group M2M OR is_public, ONCE Groups ships
 *  - fallback (default)          → is_public=true (the live public feed)
 * The group `populate`/`filters` and `isActive` are re-added when the Groups
 * feature lands on the backend (R-010). `userId` is accepted for forward-compat
 * but the membership path is not sent (it relies on the unbuilt Groups schema).
 */
export const getRecommendedTracks = async (
  page: number,
  _userId?: number | null,
  groupId?: number | null,
  includePublic?: boolean,
  styles?: readonly string[],
): Promise<RecommendedTracksResponse> => {
  // Uses the custom Query-Engine feed endpoint, which reliably filters by
  // circle (the many-to-many relation) — the content-API relation filter
  // silently returns empty. Query building lives in buildFeedQuery (tested).
  const params = buildFeedQuery(page, groupId, includePublic, undefined, styles);
  const response = await api.get(`/recommendation/feed?${params}`);
  return response.data;
};

/**
 * Refresh a track's media via Deezer when its preview link is stale (the
 * backend re-resolves and persists a fresh previewUrl). Returns the new
 * preview URL if available.
 */
export const updateTrackWithDeezer = async (isrc: string): Promise<string | undefined> => {
  const response = await api.put(`/external-service-access/deezer/track/${isrc}`);
  return response.data?.trackInfo?.previewUrl ?? response.data?.previewUrl ?? undefined;
};

/** Deezer track search. */
export const searchTracks = async (input: string, signal?: AbortSignal): Promise<Track[]> => {
  const encoded = encodeURIComponent(input);
  const response = await api.get<Track[]>(`/external-service-access/deezer/track/${encoded}`, {
    signal,
  });
  return response.data;
};
